<script lang="ts" setup>
import { PropType, computed, onBeforeUnmount, onMounted, ref } from 'vue';
import ActionContentForm from '@/components/action/ActionContentForm.vue';
import {
  TYPE_ACTION,
  TYPE_INFOGRAPHIC,
  NEW_ID,
  TYPE_INITIATIVE,
  TYPE_QUESTION,
} from '@/util/constants';
import { InfoItem } from '@/models/info-item.model';
import { Hero } from '@/models/hero.model';
import { Action, Step } from '@/models/action.model';
import { QuizQuestion } from '@/models/quiz-question.model';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { useStore } from 'vuex';
import { ContentStoreState, ContentStoreActions } from '@/stores/content.store';
import { DataResponse } from '@/services/base.service';
import { InfoGraphic } from '@/models/info-graphic.model';
import InfoGraphicContentForm from '@/components/infographic/InfoGraphicContentForm.vue';
import InitiativeContentForm from '@/components/initiative/InitiativeContentForm.vue';
import { Initiative } from '@/models/initiative.model';
import QuestionContentForm from '@/components/quiz/QuestionContentForm.vue';
import QuestionStats from '@/components/quiz/QuestionStats.vue';
import { Contact } from '@/models/contact.model';
import { Category } from '@/models/category.model';
import { Address } from '@/models/info.model';

const events = useEvents();
const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();

const props = defineProps({
  data: {
    type: Object as PropType<{
      type: string;
      contentItem:
        | InfoItem
        | Hero
        | Action
        | QuizQuestion
        | InfoGraphic
        | Initiative;
    }>,
    required: true,
  },
});

const model = ref<
  InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative
>({
  ...props.data.contentItem,
});

let currentValue = ref<
  InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative
>({ ...model.value });

const action = computed<string>(() =>
  model.value.id === NEW_ID ? 'Add' : 'Save',
);

function isInfoGraphic(
  d: InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative,
): d is InfoGraphic {
  return props.data.type == TYPE_INFOGRAPHIC;
}

function isAction(
  d: InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative,
): d is Action {
  return props.data.type == TYPE_ACTION;
}

function isInitiative(
  d: InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative,
): d is Initiative {
  return props.data.type == TYPE_INITIATIVE;
}

function isQuestion(
  d: InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative,
): d is QuizQuestion {
  return props.data.type == TYPE_QUESTION;
}

function getSimpleValue(i: any) {
  return isInitiative(i)
    ? {
        title: i.title,
        shortDescription: i.shortDescription,
        description: i.description,
        quizTitle: i.quizTitle,
        website: i.website,
        impactArea: i.impactArea,
        recommended: i.recommended,
        favourite: i.favourite,
        mainCategory: i.mainCategory.id,
        categories: [...(i.categories || [])].map((c: Category) => c.id),
        address: i.address,
        contacts: [...(i.contacts || [])].map((c: Contact) => ({
          firstName: c.firstName,
          lastName: c.lastName,
        })),
      }
    : isAction(i)
    ? {
        title: i.title,
        shortDescription: i.shortDescription,
        description: i.description,
        categories: [...(i.categories || [])].map((c: Category) => c.id),
        steps: [...(i.steps || [])].map((c: Step) => c.id),
      }
    : isQuestion(i)
    ? { title: i.title, answers: i.answers }
    : isInfoGraphic(i)
    ? { title: i.title }
    : {};
}

const isDirty = computed<boolean>(() => {
  const isDirty =
    JSON.stringify(getSimpleValue(model.value)) !==
    JSON.stringify(getSimpleValue(currentValue.value));

  store.commit(Mutations.SetDirtyState, isDirty);
  return isDirty;
});

const valid = ref<boolean>(true);

const cancel = () => {
  store.commit(Mutations.HideDrawer);
};

const save = () => saveItem(null, false);

const saveItem = async (e: unknown, close = true) => {
  let res: DataResponse<InfoItem | Hero | Action | QuizQuestion | null> = {
    status: false,
    data: null,
  };

  if (model.value.id === NEW_ID) {
    switch (props.data.type) {
      case TYPE_INFOGRAPHIC:
        res = await store.dispatch(
          ContentStoreActions.CreateInfoGraphic,
          model.value,
        );
        break;
      case TYPE_ACTION:
        res = await store.dispatch(
          ContentStoreActions.CreateAction,
          model.value,
        );
        break;
      case TYPE_INITIATIVE:
        res = await store.dispatch(
          ContentStoreActions.CreateInitiative,
          model.value,
        );
        break;
    }
  } else {
    switch (props.data.type) {
      case TYPE_INFOGRAPHIC:
        res = await store.dispatch(
          ContentStoreActions.UpdateInfoGraphic,
          model.value,
        );
        break;
      case TYPE_ACTION:
        res = await store.dispatch(
          ContentStoreActions.UpdateAction,
          model.value,
        );
        break;
      case TYPE_INITIATIVE:
        res = await store.dispatch(
          ContentStoreActions.UpdateInitiative,
          model.value,
        );
        break;
      case TYPE_QUESTION:
        res = await store.dispatch(
          ContentStoreActions.UpdateQuizQuestion,
          model.value,
        );
        break;
    }
  }

  if (res.status) {
    store.dispatch(Actions.ShowNotification, {
      message: `Saved ${props.data.type}`,
      type: 'success',
    });
    events.emit(Event.UpdateContentItem);
    if (close) {
      cancel();
    }
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: `Failed to saved ${props.data.type}`,
      type: 'error',
    });
  }

  currentValue.value = { ...model.value };
  store.commit(Mutations.SetDirtyState, false);
};

const toggleInitiativePublished = async (initiative: Initiative) => {
  if (initiative) {
    await store.dispatch(ContentStoreActions.UpdateInitiative, initiative);

    store.dispatch(Actions.ShowNotification, {
      message: !initiative.published
        ? 'Initiative is no longer published'
        : 'Initiative is published',
      type: !initiative.published ? 'info' : 'success',
    });
  }
};

const updateInitiativeStatus = async (initiative: Initiative) => {
  if (initiative) {
    const result = await store.dispatch(
      ContentStoreActions.UpdateInitiative,
      initiative,
    );

    store.dispatch(Actions.ShowNotification, {
      message: `Initiative status is now ${initiative.status}`,
      type: 'info',
    });

    model.value = { ...result.data };
    currentValue.value = { ...model.value };
    store.commit(Mutations.SetDirtyState, false);
  }
};

const toggleActionPublished = async (action: Action) => {
  if (action) {
    await store.dispatch(ContentStoreActions.UpdateAction, action);

    store.dispatch(Actions.ShowNotification, {
      message: !action.published
        ? 'Action is no longer published'
        : 'Action is published',
      type: !action.published ? 'info' : 'success',
    });
  }
};

const removeContact = async (item: Contact) => {
  if (item) {
    await store.dispatch(ContentStoreActions.RemoveContact, item);

    store.dispatch(Actions.ShowNotification, {
      message: 'Contact has been deleted',
      type: 'info',
    });
  }
};

const updateStoryContent = async () => {
  if (model.value.id !== NEW_ID) {
    switch (props.data.type) {
      case TYPE_INFOGRAPHIC:
        Object.assign(
          model.value,
          await store.dispatch(
            ContentStoreActions.GetInfoGraphic,
            props.data.contentItem.id,
          ),
        );
        break;
      case TYPE_ACTION:
        Object.assign(
          model.value,
          await store.dispatch(
            ContentStoreActions.GetAction,
            props.data.contentItem.id,
          ),
        );
        break;
      case TYPE_INITIATIVE:
        Object.assign(
          model.value,
          await store.dispatch(
            ContentStoreActions.GetInitiative,
            props.data.contentItem.id,
          ),
        );
        break;
      case TYPE_QUESTION:
        Object.assign(
          model.value,
          await store.dispatch(
            ContentStoreActions.GetQuizQuestion,
            props.data.contentItem.id,
          ),
        );
        break;
    }
  }
  currentValue.value = { ...model.value };
};

const removeItem = async () => {
  if (
    model.value &&
    model.value.id !== NEW_ID &&
    confirm(
      `Are you absolutely sure you want to delete this "${props.data.type}"?`,
    )
  ) {
    switch (props.data.type) {
      case TYPE_INFOGRAPHIC:
        store.dispatch(ContentStoreActions.DeleteInfoGraphic, model.value.id);
        break;
      case TYPE_ACTION:
        store.dispatch(ContentStoreActions.DeleteAction, model.value.id);
        break;
      case TYPE_INITIATIVE:
        store.dispatch(ContentStoreActions.DeleteInitiative, model.value.id);
        break;
    }

    store.commit(Mutations.HideDrawer);
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateStoryContent);

  updateStoryContent();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateStoryContent);
});
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveItem">
    <v-container>
      <h2>{{ $t(action) }} {{ data.type }} <CurrentLanguage /></h2>

      <InfoGraphicContentForm
        v-if="data.type === TYPE_INFOGRAPHIC && isInfoGraphic(model)"
        v-model="model"
      />
      <ActionContentForm
        v-if="data.type === TYPE_ACTION && isAction(model)"
        @update:published="toggleActionPublished"
        v-model="model"
      />
      <InitiativeContentForm
        v-if="data.type === TYPE_INITIATIVE && isInitiative(model)"
        v-model="model"
        @update:published="toggleInitiativePublished"
        @update:status="updateInitiativeStatus"
        @remove-contact="removeContact"
      />
      <QuestionContentForm
        v-if="data.type === TYPE_QUESTION && isQuestion(model)"
        v-model="model"
      />
      <QuestionStats
        v-if="data.type === TYPE_QUESTION && isQuestion(model)"
        :question="model"
      />
    </v-container>

    <v-row>
      <v-col col="2"></v-col>
      <v-col>
        <v-btn
          class="mt-2"
          type="button"
          color="warning"
          @click="removeItem"
          block
          v-if="data.type !== TYPE_QUESTION"
          >Delete</v-btn
        >
      </v-col>
      <v-col>
        <v-btn class="mt-2" type="button" @click="cancel" block>Cancel</v-btn>
      </v-col>
      <v-col>
        <v-btn class="mt-2" type="button" @click="save" color="success" block
          >{{ action }} <span v-if="isDirty">*</span></v-btn
        >
      </v-col>
      <v-col>
        <v-btn class="mt-2" type="submit" color="success" block
          >{{ action }} and close <span v-if="isDirty">*</span></v-btn
        ></v-col
      >
      <v-col col="2"></v-col>
    </v-row>
  </v-form>
</template>
